import React, { useState } from 'react';
import { Menu, X, ChevronRight, Phone, Instagram, Mail, Home, ArrowLeft } from 'lucide-react';

// TikTok Icon Component
const TikTokIcon = ({ className }) => (
  <svg 
    className={className} 
    viewBox="0 0 24 24" 
    fill="currentColor"
  >
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);
import { siteInfo, menuItems } from '../data/mock';
import TradizioneSection from '../components/TradizioneSection';
import ProdottiSection, { ServiceDetailPage } from '../components/ProdottiSection';
import GalleriaSection from '../components/GalleriaSection';
import ContattaciSection from '../components/ContattaciSection';

const HomePage = () => {
  const [currentSection, setCurrentSection] = useState('landing');
  const [menuOpen, setMenuOpen] = useState(false);
  const [selectedService, setSelectedService] = useState(null);

  const navigateTo = (section) => {
    setCurrentSection(section);
    setMenuOpen(false);
    setSelectedService(null);
  };

  const navigateToService = (serviceId) => {
    setSelectedService(serviceId);
  };

  const goBack = () => {
    if (selectedService) {
      setSelectedService(null);
    } else if (currentSection === 'menu') {
      setCurrentSection('home');
    } else {
      setCurrentSection('menu');
    }
  };

  const goHome = () => {
    setCurrentSection('home');
    setSelectedService(null);
  };

  // LANDING PAGE with START button
  if (currentSection === 'landing') {
    return (
      <div 
        className="min-h-screen bg-cover bg-no-repeat relative flex items-start justify-center pt-32"
        style={{
          backgroundImage: `url('${siteInfo.landingImage}')`,
          backgroundPosition: '65% center'
        }}
      >
        {/* START Button */}
        <button
          onClick={() => setCurrentSection('home')}
          className="group flex items-center gap-3 px-12 py-5 bg-white/90 backdrop-blur-sm rounded-full shadow-2xl hover:bg-white hover:shadow-[0_20px_60px_rgba(0,0,0,0.3)] transition-all duration-500 transform hover:scale-105"
        >
          <span className="text-2xl md:text-3xl font-semibold text-gray-700 tracking-wider">
            START
          </span>
          <ChevronRight 
            className="w-8 h-8 text-gray-500 group-hover:text-gray-700 group-hover:translate-x-1 transition-all duration-300" 
          />
        </button>
      </div>
    );
  }

  // Render navigation buttons for inner sections
  const renderNavButtons = () => (
    <div className="fixed top-4 left-4 z-50 flex gap-2">
      <button
        onClick={goBack}
        className="p-3 bg-white/90 backdrop-blur-sm rounded-full hover:bg-white shadow-md transition-all hover:shadow-lg"
      >
        <ArrowLeft className="w-5 h-5 text-stone-700" />
      </button>
      <button
        onClick={goHome}
        className="p-3 bg-white/90 backdrop-blur-sm rounded-full hover:bg-white shadow-md transition-all hover:shadow-lg"
      >
        <Home className="w-5 h-5 text-stone-700" />
      </button>
    </div>
  );

  // HOME SECTION - Light Theme with Dashboard Background
  if (currentSection === 'home') {
    return (
      <div 
        className="min-h-screen bg-center bg-no-repeat relative"
        style={{
          backgroundImage: `url('${siteInfo.dashboardImage}')`,
          backgroundSize: 'cover',
          backgroundAttachment: 'fixed'
        }}
      >
        {/* Light Overlay - More transparent to show stone colors */}
        <div className="absolute inset-0 bg-white/70" />
        
        <div className="relative z-10 min-h-screen flex flex-col">
          {/* Sticky Header with Logo and Menu */}
          <header className="sticky top-0 z-50 w-full px-6 py-4 flex items-center justify-between bg-white/70 backdrop-blur-md shadow-sm">
            {/* Logo - Left side */}
            <img 
              src={siteInfo.logo} 
              alt="ARKI Logo" 
              className="h-20 md:h-24 object-contain drop-shadow-lg"
              onError={(e) => e.target.style.display = 'none'}
            />

            {/* Menu Button - Right side */}
            <button
              onClick={() => navigateTo('menu')}
              className="p-3 hover:bg-stone-200/50 rounded-lg transition-colors"
            >
              <Menu className="w-7 h-7 text-stone-700" />
            </button>
          </header>

          {/* Main Content */}
          <main className="flex-1 flex flex-col items-center justify-center px-6 py-12">
            {/* ARKI Title - Montserrat */}
            <div className="text-center mb-8">
              <h1 className="font-montserrat text-5xl md:text-6xl font-extrabold text-stone-800 tracking-[0.15em] mb-2">
                ARKI
              </h1>
              <h2 className="font-montserrat text-xl md:text-2xl font-light text-stone-600 tracking-[0.2em] uppercase">
                Opere In Pietra
              </h2>
              <div className="w-full max-w-[280px] md:max-w-[320px] h-0.5 bg-amber-700 mt-4 mx-auto" />
            </div>
            
            {/* Subtitle - Cinzel */}
            <p className="font-cinzel text-lg md:text-xl text-amber-800 text-center tracking-wide mb-6 max-w-lg">
              {siteInfo.subtitle}
            </p>
            
            {/* Description */}
            <p className="text-stone-600 text-center text-base md:text-lg leading-relaxed max-w-md mb-10">
              {siteInfo.description}
            </p>

            {/* Social Icons with Labels and Borders */}
            <div className="flex flex-col sm:flex-row flex-wrap justify-center gap-4">
              <a 
                href={siteInfo.whatsapp} 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center gap-3 px-6 py-3 bg-white/80 border-2 border-green-600 rounded-lg hover:bg-green-50 transition-colors group shadow-sm"
              >
                <Phone className="w-5 h-5 text-green-600" />
                <span className="text-green-700 font-medium">WhatsApp</span>
              </a>
              <a 
                href={siteInfo.instagram} 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center gap-3 px-6 py-3 bg-white/80 rounded-lg hover:bg-pink-50 transition-colors group shadow-sm relative"
                style={{
                  border: '2px solid transparent',
                  backgroundImage: 'linear-gradient(white, white), linear-gradient(45deg, #f09433, #e6683c, #dc2743, #cc2366, #bc1888, #833ab4, #5851db, #405de6)',
                  backgroundOrigin: 'border-box',
                  backgroundClip: 'padding-box, border-box'
                }}
              >
                <Instagram className="w-5 h-5 text-pink-600" />
                <span className="text-pink-700 font-medium">Instagram</span>
              </a>
              <a 
                href={`mailto:${siteInfo.email}`}
                className="flex items-center gap-3 px-6 py-3 bg-white/80 border-2 border-stone-500 rounded-lg hover:bg-stone-100 transition-colors group shadow-sm"
              >
                <Mail className="w-5 h-5 text-stone-600" />
                <span className="text-stone-700 font-medium">Email</span>
              </a>
              <a 
                href={siteInfo.tiktok} 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center gap-3 px-6 py-3 bg-white/80 border-2 border-black rounded-lg hover:bg-gray-100 transition-colors group shadow-sm"
              >
                <TikTokIcon className="w-5 h-5 text-black" />
                <span className="text-black font-medium">TikTok</span>
              </a>
            </div>
          </main>
        </div>
      </div>
    );
  }

  // MENU SECTION - Light Theme
  if (currentSection === 'menu') {
    return (
      <div 
        className="min-h-screen bg-cover bg-center relative"
        style={{
          backgroundImage: `url('${siteInfo.dashboardImage}')`
        }}
      >
        {/* Overlay */}
        <div className="absolute inset-0 bg-white/85 backdrop-blur-sm" />
        
        <div className="relative z-10 min-h-screen flex flex-col">
          {/* Sticky Header */}
          <header className="sticky top-0 z-50 w-full px-6 py-4 flex items-center justify-between bg-white/70 backdrop-blur-md shadow-sm">
            {/* Logo - Left side */}
            <img 
              src={siteInfo.logo} 
              alt="ARKI Logo" 
              className="h-20 md:h-24 object-contain drop-shadow-lg"
              onError={(e) => e.target.style.display = 'none'}
            />
            
            {/* Close Button - Right side */}
            <button
              onClick={() => navigateTo('home')}
              className="p-3 hover:bg-stone-200/50 rounded-lg transition-colors"
            >
              <X className="w-7 h-7 text-stone-700" />
            </button>
          </header>

          {/* Menu Items */}
          <div className="flex-1 flex flex-col justify-center px-8 md:px-16 py-12">
            <nav className="space-y-6">
              {menuItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => navigateTo(item.id)}
                  className="flex items-center gap-4 text-left w-full group"
                >
                  <ChevronRight className="w-5 h-5 text-amber-700 group-hover:translate-x-2 transition-transform" />
                  <span className="font-cinzel text-stone-700 text-xl md:text-2xl tracking-[0.1em] hover:text-amber-800 transition-colors">
                    {item.label}
                  </span>
                </button>
              ))}
            </nav>
          </div>
        </div>
      </div>
    );
  }

  // TRADIZIONE SECTION
  if (currentSection === 'tradizione') {
    return (
      <div className="min-h-screen bg-stone-50">
        {renderNavButtons()}
        <TradizioneSection />
      </div>
    );
  }

  // PRODOTTI SECTION
  if (currentSection === 'prodotti') {
    if (selectedService) {
      return (
        <div className="min-h-screen bg-stone-50">
          <ServiceDetailPage 
            serviceId={selectedService} 
            onBack={goBack}
            onHome={goHome}
            onNavigateToGallery={() => navigateTo('galleria')}
          />
        </div>
      );
    }
    return (
      <div className="min-h-screen bg-stone-50">
        {renderNavButtons()}
        <ProdottiSection onNavigateToService={navigateToService} />
      </div>
    );
  }

  // GALLERIA SECTION
  if (currentSection === 'galleria') {
    return (
      <div className="min-h-screen bg-stone-50">
        {renderNavButtons()}
        <GalleriaSection />
      </div>
    );
  }

  // CONTATTACI SECTION
  if (currentSection === 'contattaci') {
    return (
      <div className="min-h-screen bg-stone-50">
        {renderNavButtons()}
        <ContattaciSection />
      </div>
    );
  }

  return null;
};

export default HomePage;
